import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { DynamicFormComponent } from '../../../shared/dynamic-form/dynamic-form.component';
import { FieldOptions } from '../../../shared/dynamic-form/interfaces/field-options.interface';
import { DisplayLabel } from '../../../shared/models/displayLabel';
import { BarrierStatus, labelsByBarrierStatus } from '../../../shared/models/enums/barrier-status.enum';
import { Entrepreneur } from '../../../shared/models/enums/entrepreneur.enum';
import { AssetService } from '../../../shared/services/asset.service';
import { BarrierService } from '../../../shared/services/barrier.service';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { BeneficiaryEmploymentService } from '../../../shared/services/beneficiary-employment.service';
import { DegreeService } from '../../../shared/services/degree.service';
import { JobTypeService } from '../../../shared/services/job-type.service';
import { JobService } from '../../../shared/services/job.service';
import { QualificationTypeService } from '../../../shared/services/qualification-type.service';
import { StringUtils } from '../../../shared/services/stringUtils.service';
import { TrainingAreaService } from '../../../shared/services/trainingArea.service';
import { TransportModeService } from '../../../shared/services/transport-mode.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { DateValidators } from '../../../shared/validators/date.validators';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { BeneficiaryEmploymentDto } from './dto/beneficiary-employment.dto';
import { EmploymentAssetDto } from './dto/employment-asset.dto';
import { EmploymentBarrierDto } from './dto/employment-barrier.dto';
import { EmploymentQualificationDto } from './dto/employment-qualification.dto';
import { EmploymentTransportModeDto } from './dto/employment-transport.dto';
import { JobCategoryDto } from './dto/job-category.dto';

@Component({
  selector: 'app-employment',
  templateUrl: './employment.component.html',
  styleUrls: ['./employment.component.scss'],
  standalone: false,
})
export class EmploymentComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  stringAscOrder = StringUtils.stringAscOrder;

  public submitted: boolean = false;

  @ViewChild('qualificationsComponent', { static: true })
  public qualificationsComponent: DynamicFormComponent;

  @ViewChild('transportsComponent', { static: true })
  public transportsComponent: DynamicFormComponent;

  @ViewChild('barriersComponent', { static: true })
  public barriersComponent: DynamicFormComponent;

  @ViewChild('assetsComponent', { static: true })
  public assetsComponent: DynamicFormComponent;

  @ViewChild('jobsComponent', { static: true })
  public jobsComponent: DynamicFormComponent;

  public beneficiaryEmployment: BeneficiaryEmploymentDto;
  public employmentQualifications: EmploymentQualificationDto[] = [];
  public employmentTransportModes: EmploymentTransportModeDto[] = [];
  public employmentBarriers: EmploymentBarrierDto[] = [];
  public employmentAssets: EmploymentAssetDto[] = [];

  public employmentForm: FormGroup<{
    id: FormControl<number>;
    accreditationSIAE: FormControl<boolean>;
    accreditationSIAEDate: FormControl<Date>;
    rqth: FormControl<boolean>;
    expiryRqthDate: FormControl<Date>;
    entrepreneur: FormControl<Entrepreneur>;
    beginDateEntrepreneurship: FormControl<Date>;
    endDateEntrepreneurship: FormControl<Date>;
    sectorId: FormControl<number>;
    degreeId: FormControl<number>;
    trainingAreaIds: FormControl<number[]>;
    studyAbroad: FormControl<boolean>;
    wantedJobTypeId: FormControl<number>;
  }>;

  public degrees: DisplayLabel[] = [];
  public trainingAreas: DisplayLabel[] = [];
  public jobTypes: DisplayLabel[] = [];

  public qualificationTypes: DisplayLabel[] = [];
  public qualificationFields: (string | FieldOptions)[] = [];

  public transportModes: DisplayLabel[] = [];
  public transportModeFields: (string | FieldOptions)[] = [];

  public barriers: DisplayLabel[] = [];
  public barrierFields: (string | FieldOptions)[] = [];

  public assets: DisplayLabel[] = [];
  public assetFields: (string | FieldOptions)[] = [];

  public sectors: JobCategoryDto[] = [];
  public employmentJobs: { sector: JobCategoryDto; domain: JobCategoryDto; job: JobCategoryDto }[] = [];
  public jobFields: (string | FieldOptions)[] = [];

  public readonly barrierStatuses: Map<BarrierStatus, string> = labelsByBarrierStatus;
  public readonly StringUtils: StringUtils = StringUtils;

  constructor(
    private readonly fb: FormBuilder,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly beneficiaryEmploymentService: BeneficiaryEmploymentService,
    private readonly trainingAreaService: TrainingAreaService,
    private readonly degreeService: DegreeService,
    private readonly qualificationTypeService: QualificationTypeService,
    private readonly transportModeService: TransportModeService,
    private readonly barrierService: BarrierService,
    private readonly jobService: JobService,
    private readonly toastService: ToastService,
    private readonly assetService: AssetService,
    private readonly jobTypeService: JobTypeService,
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    await this.initList();
    await this.initEmploymentForm();
    this.initFields();
  }

  private initFields(): void {
    this.qualificationFields = this.getQualificationFields();
    this.transportModeFields = this.getTransportModeFields();
    this.barrierFields = this.getBarrierFields();
    this.assetFields = this.getAssetFields();
    this.jobFields = this.getJobFields();
  }

  private async initEmploymentForm(): Promise<void> {
    if (this.beneficiary.employmentId) {
      await this.getBeneficiaryEmployment(this.beneficiary.employmentId);
    } else {
      this.beneficiaryEmployment = new BeneficiaryEmploymentDto();
    }

    this.employmentForm = this.fb.group(
      {
        id: new FormControl<number>(this.beneficiaryEmployment.id),
        accreditationSIAE: new FormControl<boolean>(this.beneficiaryEmployment.accreditationSIAE, [Validators.required]),
        accreditationSIAEDate: new FormControl<Date>({
          value: this.beneficiaryEmployment.accreditationSIAEDate,
          disabled: !this.beneficiaryEmployment.accreditationSIAE,
        }),
        rqth: new FormControl<boolean>(this.beneficiaryEmployment.rqth, [Validators.required]),
        expiryRqthDate: new FormControl<Date>({
          value: this.beneficiaryEmployment.expiryRqthDate,
          disabled: !this.beneficiaryEmployment.rqth,
        }),
        entrepreneur: new FormControl<Entrepreneur>(this.beneficiaryEmployment.entrepreneur, [Validators.required]),
        beginDateEntrepreneurship: new FormControl<Date>({
          value: this.beneficiaryEmployment.beginDateEntrepreneurship,
          disabled:
            this.beneficiaryEmployment.entrepreneur !== Entrepreneur.BusinessOwner &&
            this.beneficiaryEmployment.entrepreneur !== Entrepreneur.Freelancer,
        }),
        endDateEntrepreneurship: new FormControl<Date>({
          value: this.beneficiaryEmployment.endDateEntrepreneurship,
          disabled:
            this.beneficiaryEmployment.entrepreneur !== Entrepreneur.BusinessOwner &&
            this.beneficiaryEmployment.entrepreneur !== Entrepreneur.Freelancer,
        }),
        sectorId: new FormControl<number>({
          value: this.beneficiaryEmployment.sectorId,
          disabled:
            this.beneficiaryEmployment.entrepreneur !== Entrepreneur.BusinessOwner &&
            this.beneficiaryEmployment.entrepreneur !== Entrepreneur.Freelancer,
        }),
        degreeId: new FormControl<number>(this.beneficiaryEmployment.degreeId, [Validators.required]),
        trainingAreaIds: new FormControl<number[]>(this.beneficiaryEmployment.trainingAreaIds),
        studyAbroad: new FormControl<boolean>(this.beneficiaryEmployment.studyAbroad, [Validators.required]),
        wantedJobTypeId: new FormControl<number>(this.beneficiaryEmployment.wantedJobTypeId),
      },
      {
        validators: [DateValidators.datesOrderValidator('beginDateEntrepreneurship', 'endDateEntrepreneurship')],
      },
    );
    this.employmentForm.controls.accreditationSIAE.setValue(this.beneficiaryEmployment.accreditationSIAE);
    this.employmentForm.controls.rqth.setValue(this.beneficiaryEmployment.rqth);

    this.employmentForm.controls.accreditationSIAE.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => this.handleASIAEChange(value));
    this.employmentForm.controls.rqth.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => this.handleRqthChange(value));
    this.employmentForm.controls.entrepreneur.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: Entrepreneur) => this.handleEntrepreneurshipChange(value));

    this.setDateSIAE();
    this.setDateRQTH();
    this.setEntrepreneurship();
  }

  private async initList(): Promise<void> {
    this.degrees = await this.degreeService.findAll();
    this.trainingAreas = await this.trainingAreaService.findAll();
    this.qualificationTypes = await this.qualificationTypeService.findAll();
    this.transportModes = await this.transportModeService.findAll();
    this.barriers = await this.barrierService.findAll();
    this.sectors = await this.jobService.findAll();
    this.jobTypes = await this.jobTypeService.findAll();
    this.assets = await this.assetService.findAll();
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;

    this.setEntrepreneurship();
    this.resetValidatorEntrepreneurship();

    this.setDateSIAE();
    this.resetValidatorSIAE();

    this.setDateRQTH();
    this.resetValidatorRQTH();

    if (
      this.employmentForm.valid &&
      this.qualificationsComponent.form.valid &&
      this.transportsComponent.form.valid &&
      this.barriersComponent.form.valid &&
      this.jobsComponent.form.valid &&
      this.assetsComponent.form.valid
    ) {
      const employmentUpdated: BeneficiaryEmploymentDto = new BeneficiaryEmploymentDto({
        id: this.employmentForm.controls.id.value,
        accreditationSIAE: this.employmentForm.controls.accreditationSIAE.value,
        accreditationSIAEDate: this.employmentForm.controls.accreditationSIAEDate.value,
        rqth: this.employmentForm.controls.rqth.value,
        expiryRqthDate: this.employmentForm.controls.expiryRqthDate.value,
        entrepreneur: this.employmentForm.controls.entrepreneur.value,
        beginDateEntrepreneurship: this.employmentForm.controls.beginDateEntrepreneurship.value,
        endDateEntrepreneurship: this.employmentForm.controls.endDateEntrepreneurship.value,
        sectorId: this.employmentForm.controls.sectorId.value,
        degreeId: this.employmentForm.controls.degreeId.value,
        trainingAreaIds: this.employmentForm.controls.trainingAreaIds.value,
        wantedJobTypeId: this.employmentForm.controls.wantedJobTypeId.value,
        studyAbroad: this.employmentForm.controls.studyAbroad.value,
        qualificationTypes: this.fillQualification(this.qualificationsComponent.form.controls.items.value),
        transportModes: this.fillTransportMode(this.transportsComponent.form.controls.items.value),
        barriers: this.fillBarrier(this.barriersComponent.form.controls.items.value),
        assets: this.fillAsset(this.assetsComponent.form.controls.items.value),
        jobIds: (this.jobsComponent.form.controls.items.value || []).map((value) => value.job.id),
      });
      const beneficiaryEmploymentSaved: BeneficiaryEmploymentDto = await this.beneficiaryEmploymentService.updateEmploymentBeneficiary(
        this.beneficiary.id,
        employmentUpdated,
      );
      if (this.beneficiary.employmentId) {
        await this.getBeneficiaryEmployment(this.beneficiary.employmentId);
        this.toastService.show("L'emploi du bénéficiaire a été modifié avec succès.", 'success');
      } else {
        await this.getBeneficiaryEmployment(beneficiaryEmploymentSaved.id);
        this.toastService.show("L'emploi du bénéficiaire a été créé avec succès.", 'success');
      }
    } else {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    }
  }

  public async cancel(): Promise<void> {
    await this.getBeneficiaryEmployment(this.beneficiaryEmployment.id);
    this.employmentForm.patchValue(this.beneficiaryEmployment, { emitEvent: true });
    this.submitted = false;
  }

  public resetValidatorSIAE(): void {
    if (this.employmentForm.controls.accreditationSIAE.value === false) {
      this.employmentForm.controls.accreditationSIAEDate.clearValidators();
      this.employmentForm.controls.accreditationSIAEDate.updateValueAndValidity();
    }
  }

  public resetValidatorRQTH(): void {
    if (this.employmentForm.controls.rqth.value === false) {
      this.employmentForm.controls.expiryRqthDate.clearValidators();
      this.employmentForm.controls.expiryRqthDate.updateValueAndValidity();
    }
  }

  public resetValidatorEntrepreneurship(): void {
    if (this.employmentForm.controls.entrepreneur.value === Entrepreneur.No) {
      this.employmentForm.controls.beginDateEntrepreneurship.clearValidators();
      this.employmentForm.controls.endDateEntrepreneurship.clearValidators();
      this.employmentForm.controls.sectorId.clearValidators();

      this.employmentForm.controls.beginDateEntrepreneurship.updateValueAndValidity();
      this.employmentForm.controls.endDateEntrepreneurship.updateValueAndValidity();
      this.employmentForm.controls.sectorId.updateValueAndValidity();
    }
  }

  private setDateSIAE(): void {
    if (this.employmentForm.controls.accreditationSIAE.value) {
      this.employmentForm.controls.accreditationSIAEDate.setValidators([Validators.required, DateValidators.dateFormatValidator]);
      this.employmentForm.controls.accreditationSIAEDate.updateValueAndValidity();
    }
  }

  private setDateRQTH(): void {
    if (this.employmentForm.controls.rqth.value) {
      this.employmentForm.controls.expiryRqthDate.setValidators([Validators.required, DateValidators.dateFormatValidator]);
      this.employmentForm.controls.expiryRqthDate.updateValueAndValidity();
    }
  }

  private setEntrepreneurship(): void {
    if (this.employmentForm.controls.entrepreneur.value && this.employmentForm.controls.entrepreneur.value !== Entrepreneur.No) {
      this.employmentForm.controls.beginDateEntrepreneurship.setValidators([Validators.required, DateValidators.dateFormatValidator]);
      this.employmentForm.controls.endDateEntrepreneurship.setValidators([DateValidators.dateFormatValidator]);
      this.employmentForm.controls.sectorId.setValidators([Validators.required]);

      this.employmentForm.controls.beginDateEntrepreneurship.updateValueAndValidity();
      this.employmentForm.controls.endDateEntrepreneurship.updateValueAndValidity();
      this.employmentForm.controls.sectorId.updateValueAndValidity();
    }
  }

  private readonly fillQualification: (values: any[]) => EmploymentQualificationDto[] = (values: any[]): EmploymentQualificationDto[] =>
    (values || []).map(
      (value) =>
        new EmploymentQualificationDto(
          value.id ?? undefined,
          value.qualificationDate,
          value.qualificationDesc,
          value.qualificationTypeId,
          value.beneficiaryEmploymentId ?? undefined,
        ),
    );

  private readonly fillTransportMode: (values: any[]) => EmploymentTransportModeDto[] = (values: any[]): EmploymentTransportModeDto[] =>
    (values || []).map(
      (value) =>
        new EmploymentTransportModeDto(value.id ?? undefined, value.description, value.transportModeId, value.beneficiaryEmploymentId ?? undefined),
    );

  private readonly fillBarrier: (values: any[]) => EmploymentBarrierDto[] = (values: any[]): EmploymentBarrierDto[] =>
    (values || []).map(
      (value) =>
        new EmploymentBarrierDto(
          value.id ?? undefined,
          value.description,
          value.barrierStatus,
          value.barrierId,
          value.beneficiaryEmploymentId ?? undefined,
        ),
    );

  private readonly fillAsset: (values: any[]) => EmploymentAssetDto[] = (values: any[]): EmploymentAssetDto[] =>
    (values || []).map(
      (value) => new EmploymentAssetDto(value.id ?? undefined, value.assetId, value.description, value.beneficiaryEmploymentId ?? undefined),
    );

  private async getBeneficiaryEmployment(idEmployment: number): Promise<BeneficiaryEmploymentDto> {
    if (idEmployment) {
      this.beneficiaryEmployment = await this.beneficiaryEmploymentService.findById(idEmployment);
      this.employmentQualifications = this.beneficiaryEmployment?.qualificationTypes || [];
      this.employmentBarriers = this.beneficiaryEmployment?.barriers || [];
      this.employmentTransportModes = this.beneficiaryEmployment?.transportModes || [];
      (this.beneficiaryEmployment?.jobIds || []).forEach((jobId) => this.initEmploymentJob(jobId));
      this.employmentAssets = this.beneficiaryEmployment?.assets || [];

      return this.beneficiaryEmployment;
    }
  }

  private initEmploymentJob(jobId: number): void {
    (this.sectors || []).forEach((sector) => {
      (sector.subValues || []).forEach((domain) => {
        (domain.subValues || []).forEach((job) => {
          if (job.id === jobId) {
            this.employmentJobs.push({ sector, domain, job });
          }
        });
      });
    });
  }

  private handleRqthChange(value: boolean): void {
    const control: AbstractControl = this.employmentForm.controls.expiryRqthDate;
    if (value) {
      control.enable();
    } else {
      control.disable();
      control.setValue(undefined);
    }
  }

  private handleASIAEChange(value: boolean): void {
    const control: AbstractControl = this.employmentForm.controls.accreditationSIAEDate;
    if (value) {
      control.enable();
    } else {
      control.disable();
      control.setValue(undefined);
    }
  }

  private handleEntrepreneurshipChange(value: Entrepreneur): void {
    const controlBeginDate: AbstractControl = this.employmentForm.controls.beginDateEntrepreneurship;
    const controlEndDate: AbstractControl = this.employmentForm.controls.endDateEntrepreneurship;
    const controlType: AbstractControl = this.employmentForm.controls.sectorId;
    if (value && value !== Entrepreneur.No) {
      controlBeginDate.enable();
      controlEndDate.enable();
      controlType.enable();
    } else {
      controlBeginDate.disable();
      controlEndDate.disable();
      controlType.disable();

      controlBeginDate.setValue(undefined);
      controlEndDate.setValue(undefined);
      controlType.setValue(undefined);
    }
  }

  public readonly handleSectorChange: (group: FormGroup) => void = (group: FormGroup): void => {
    group.controls.domain.setValue(undefined);
  };

  public readonly handleDomainChange: (group: FormGroup) => void = (group: FormGroup): void => {
    if (group.controls.domain.value?.subValues?.length === 1) {
      group.controls.job.setValue(group.controls.domain.value?.subValues[0]);
    } else {
      group.controls.job.setValue(undefined);
    }
  };

  private getQualificationFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'qualificationDate', validators: [Validators.required] },
      { value: 'qualificationTypeId', validators: [Validators.required] },
      { value: 'qualificationDesc', validators: [] },
      { value: 'beneficiaryEmploymentId', validators: [] },
    ];
  }

  private getTransportModeFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'transportModeId', validators: [Validators.required] },
      { value: 'description', validators: [] },
      { value: 'beneficiaryEmploymentId', validators: [] },
    ];
  }

  private getBarrierFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'barrierId', validators: [Validators.required] },
      { value: 'description', validators: [] },
      { value: 'barrierStatus', validators: [Validators.required] },
      { value: 'beneficiaryEmploymentId', validators: [] },
    ];
  }

  private getJobFields(): (string | FieldOptions)[] {
    return [
      { value: 'sector', validators: [Validators.required] },
      { value: 'domain', validators: [Validators.required] },
      { value: 'job', validators: [Validators.required] },
    ];
  }

  private readonly getAssetFields: () => (string | FieldOptions)[] = (): (string | FieldOptions)[] => [
    { value: 'id', validators: [] },
    { value: 'assetId', validators: [Validators.required] },
    { value: 'description', validators: [] },
    { value: 'beneficiaryEmploymentId', validators: [] },
  ];
}
